/** No letter before: the start of a word. */
const START = '(?<!\\p{L})';
/** No letter after: the end of a word. */
const END = '(?!\\p{L})';
const word = (body) => new RegExp(`${START}${body}`, 'giu');
/** The lectern's own buttons: a `knop` naming one of them is not a headset word. */
export const LECTERN_BUTTONS = ['Verder', 'Terug', 'Nog eens', 'Voorlezen'];
export const HEADSET_WORDS = [
    {
        pattern: word(`(?:ge)?klik\\p{L}*`),
        says: "'klik' — de leerling wijst aan en drukt",
    },
    {
        pattern: word(`aan(?:ge)?klik\\p{L}*`),
        says: "'aanklikken' — de leerling wijst aan en drukt",
    },
    {
        // Only "tik" said to the pupil: "Tik (weer) op…", "tik … aan", "te tikken", "aantikken", "je tikt",
        // "tikken op". Never a clock that "tikt" (review of Task 5).
        pattern: new RegExp([
            `(?<=^|[.!?:;,]\\s*|${START}en\\s+)tik${END}`,
            `${START}aan(?:ge)?tik\\p{L}*`,
            `${START}(?:ge)?tik(?:ken)?\\s+(?:op|aan|erop|eraan)${END}`,
            `${START}te\\s+tikken${END}`,
            `${START}je\\s+tikt${END}`,
        ].join('|'), 'giu'),
        says: "'tik' — er is geen scherm: de leerling wijst aan en drukt",
    },
    { pattern: word(`swip\\p{L}*`), says: "'swipe' — er is geen scherm om over te vegen" },
    { pattern: word(`scroll\\p{L}*`), says: "'scroll' — er is niets om door te scrollen" },
    {
        pattern: word(`(?:sleep|sleept|slepen)${END}`),
        says: "'sleep' — de leerling kan niets verslepen",
    },
    {
        pattern: word(`(?:in|uit)?zoom\\p{L}*`),
        says: "'zoom' — de leerling kan niet zoomen; het platform zet hem dichtbij",
    },
    {
        pattern: word(`draai\\s+(?:het|de|dit|dat)\\s+(?:\\p{L}+\\s+)?model${END}`),
        says: "'draai het model' — het platform draait het model, de leerling niet",
    },
    {
        // Imperative "draai … rond" with a thing to turn: "Draai de haai langzaam rond". Not "Draai langzaam
        // rond" or "draai je hoofd": the pupil can turn round (re-judge, 24 Oct).
        // The window stays in the clause: no punctuation and no "en" between (review of Task 5).
        pattern: word(`draai\\s+(?:het|de|dit|dat|deze)\\s(?:(?!\\sen\\s)[^.!?,;:]){0,40}?\\s*rond${END}`),
        says: "'draai … rond' — het platform draait het model, de leerling niet",
    },
    {
        pattern: word(`rode\\s+stip\\p{L}*`),
        says: "'rode stip' — de punten zijn ⓘ-tekens, geen stippen",
    },
    {
        pattern: word(`gemarkeerde?\\s+(?:plek|punt)\\p{L}*`),
        says: "'gemarkeerde plek' — de punten zijn ⓘ-tekens boven het model",
    },
    {
        pattern: word(`pijl(?:tje|tjes|en|s)?${END}`),
        says: "'pijl' — er staan geen pijlen in de les",
        unlessObject: 'pijl',
    },
    {
        // Singular only: "knoppen" of a rose are buds (review of Task 5).
        pattern: word(`(?:\\p{L}+-?)?knop${END}`),
        says: "'knop' — de lessenaar heeft alleen Verder, Terug, Nog eens en Voorlezen",
    },
    {
        pattern: word(`loop\\s+naar${END}`),
        says: "'loop naar' — de leerling blijft staan; alleen het platform verplaatst hem",
        onlyWithoutTeleport: true,
    },
    {
        pattern: word(`ga\\s+naar${END}`),
        says: "'ga naar' — de leerling blijft staan; alleen het platform verplaatst hem",
        onlyWithoutTeleport: true,
    },
];
const BUTTON = `(?:${LECTERN_BUTTONS.join('|')})`;
/** A button's name, capitalised as the lectern shows it, right after "knop": "de knop 'Verder'". */
const BUTTON_AFTER = new RegExp(`^\\s*['"‘“]?${BUTTON}${END}`, 'u');
/** A button's name right before "knop": "de Voorlezen-knop", "de Verder knop". */
const BUTTON_BEFORE = new RegExp(`${START}${BUTTON}['"’”]?[\\s-]?knop$`, 'u');
/** A `knop` that is one of the lectern's buttons, named beside it (case-sensitive: "verder" is Dutch). */
function isLecternKnop(text, at, length) {
    return (BUTTON_AFTER.test(text.slice(at + length)) ||
        BUTTON_BEFORE.test(text.slice(Math.max(0, at - 16), at + length)));
}
/**
 * The `says` of each headset word in `text`, once each, in the list's order. `teleport`: the step moves the
 * pupil, so "loop naar" and "ga naar" are allowed there. `objects`: the lesson's object ids and labels, when
 * known, so a word for a thing the lesson does show (a pijl shape) is allowed.
 */
export function headsetWordsIn(text, options) {
    const found = [];
    /** What is already reported: one phrase ("Draai het model rond") is one problem. */
    const taken = [];
    const overlaps = (from, to) => taken.some(([start, end]) => from < end && to > start);
    const objects = (options.objects ?? []).map((name) => name.toLowerCase());
    for (const entry of HEADSET_WORDS) {
        if (entry.onlyWithoutTeleport === true && options.teleport)
            continue;
        const shown = entry.unlessObject;
        if (shown !== undefined && objects.some((name) => name.includes(shown)))
            continue;
        const pattern = new RegExp(entry.pattern.source, entry.pattern.flags);
        for (const match of text.matchAll(pattern)) {
            const end = match.index + match[0].length;
            if (overlaps(match.index, end))
                continue;
            if (entry.says.startsWith("'knop'") && isLecternKnop(text, match.index, match[0].length)) {
                continue;
            }
            found.push(entry.says);
            taken.push([match.index, end]);
            break;
        }
    }
    return found;
}
//# sourceMappingURL=headset-words.js.map